import React, { useMemo, useState } from "react";
import {
  AnalyticsGranularity,
  ClicksTrendingMetric,
  ClicksTrendingResponse,
} from "../../api/analyticsAdmin";
import { formatBucketTick } from "../../helpers/searchTrend";
import { buildBumpChart, latestBucket } from "../../helpers/searchTrending";
import SectionBody from "./SectionBody";
import { SectionState } from "./useAnalyticsStream";
import TrendingNow from "./trending/TrendingNow";
import TrendingBoard from "./trending/TrendingBoard";
import TrendingBumpChart from "./trending/TrendingBumpChart";

type TrendingView = "now" | "board" | "bump";

const VIEWS: { key: TrendingView; label: string }[] = [
  { key: "now", label: "Trending now" },
  { key: "board", label: "Board" },
  { key: "bump", label: "Bump chart" },
];

const METRICS: { key: ClicksTrendingMetric; label: string }[] = [
  { key: "Opens", label: "Opens" },
  { key: "Outbounds", label: "Outbounds" },
  { key: "Conversions", label: "Conversions" },
];

const GRANULARITIES: AnalyticsGranularity[] = ["Day", "Week", "Month"];
const TOP_NS = [5, 10, 20];

// The three controls that shape the request live on the page, which owns the
// batch; the view switch stays here because it only picks between renderings of
// data already in hand.
const ClicksTrending: React.FC<{
  section: SectionState<ClicksTrendingResponse>;
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
  topN: number;
  onTopNChange: (topN: number) => void;
  metric: ClicksTrendingMetric;
  onMetricChange: (metric: ClicksTrendingMetric) => void;
}> = ({
  section,
  granularity,
  onGranularityChange,
  topN,
  onTopNChange,
  metric,
  onMetricChange,
}) => {
  const [view, setView] = useState<TrendingView>("now");

  const buckets = useMemo(
    () => (section.status === "ok" ? section.data.buckets : []),
    [section]
  );
  const newest = useMemo(() => latestBucket(buckets), [buckets]);
  const bump = useMemo(() => buildBumpChart(buckets, topN), [buckets, topN]);
  const metricLabel = METRICS.find((m) => m.key === metric)?.label ?? metric;

  return (
    <section className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-medium">Trending businesses</h3>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  view === v.key
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <select
            value={metric}
            onChange={(e) => onMetricChange(e.target.value as ClicksTrendingMetric)}
            className="p-2 border border-gray-300 rounded-lg text-sm"
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
          <label className="text-sm text-gray-600">
            Top{" "}
            <select
              value={topN}
              onChange={(e) => onTopNChange(Number(e.target.value))}
              className="p-2 border border-gray-300 rounded-lg text-sm ml-1"
            >
              {TOP_NS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-1">
            {GRANULARITIES.map((g) => (
              <button
                key={g}
                onClick={() => onGranularityChange(g)}
                className={`px-3 py-1.5 text-sm rounded-lg border ${
                  granularity === g
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      <SectionBody
        section={section}
        isEmpty={() => buckets.length === 0}
        emptyMessage="No trending businesses for this range."
      >
        {() => (
          <>
            {view === "now" && newest && (
              <TrendingNow
                bucket={newest}
                label={`Top businesses · ${metricLabel} · ${formatBucketTick(
                  newest.bucketStart,
                  granularity
                )}`}
                nounLabel="Business"
                valueLabel={metricLabel}
              />
            )}
            {view === "board" && (
              <TrendingBoard buckets={buckets} granularity={granularity} />
            )}
            {view === "bump" && (
              <TrendingBumpChart chart={bump} granularity={granularity} topN={topN} />
            )}
          </>
        )}
      </SectionBody>
    </section>
  );
};

export default ClicksTrending;
